import { useCallback, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  redeemReferral,
  updateProfile,
  type RedeemReferralResponse,
  type UpdateProfileRequest,
} from "../../lib/api/contractor";
import { useAuth } from "../../lib/auth";
import type { ServiceAreaGeo } from "../../lib/types/models";
import {
  Banner,
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  PageHead,
  TextArea,
  TextField,
  useToast,
  focusFirstInvalid,
} from "@ui/react";
import { ChevronLeftIcon } from "@ui/react/icons";
import { SkillsPicker } from "../../components/SkillsPicker";
import { ServiceAreaPicker } from "../../components/ServiceAreaPicker";
import { errorMessage } from "../../lib/errorMessage";

const BIO_LIMIT = 500;

interface Errors {
  firstName?: string;
  lastName?: string;
  hourlyRate?: string;
  bio?: string;
}

export function EditProfileScreen() {
  const { contractor, setContractor } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);

  // The gate guarantees a contractor before this route renders, so the form is
  // seeded from context rather than re-fetching /profile — there is nothing a
  // second read would add, and it would flash an empty form first.
  const [firstName, setFirstName] = useState(contractor?.firstName ?? "");
  const [lastName, setLastName] = useState(contractor?.lastName ?? "");
  const [phoneNumber, setPhoneNumber] = useState(contractor?.phoneNumber ?? "");
  const [businessName, setBusinessName] = useState(contractor?.businessName ?? "");
  const [hourlyRate, setHourlyRate] = useState(
    contractor?.hourlyRate != null ? String(contractor.hourlyRate) : "",
  );
  const [bio, setBio] = useState(contractor?.bio ?? "");
  const [isAvailable, setIsAvailable] = useState(contractor?.isAvailable ?? true);
  const [skills, setSkills] = useState<string[]>(contractor?.skills ?? []);
  const [areas, setAreas] = useState<ServiceAreaGeo[]>(contractor?.serviceAreaGeos ?? []);

  const [errors, setErrors] = useState<Errors>({});
  // Set on the first submit attempt, so the "you need at least one" notes on the
  // two pickers appear once the contractor has actually tried to save rather
  // than scolding them the moment the form opens.
  const [attempted, setAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [confirmIncomplete, setConfirmIncomplete] = useState(false);

  /* The back link at the top is the one exit that discards silently. A ref
     rather than state: nothing renders differently while the form is dirty.
     The form's own onChange catches every field (React's onChange IS the input
     event, and it bubbles); the two pickers mutate through callbacks instead,
     so they set it themselves. Cancel stays unguarded — discarding is what
     Cancel means. */
  const dirty = useRef(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  // Onboarding is the only OTHER place a referral code can be entered
  // (ProfileCompletionScreen), and once that gate passes it never comes back —
  // this is the sole entry point after sign-up. Kept separate from the
  // `save()`/`PATCH /profile` flow below: redeeming is its own endpoint with
  // its own success/conflict shape, not a profile field.
  const [referralCode, setReferralCode] = useState("");
  const [referralSubmitting, setReferralSubmitting] = useState(false);
  const [referralError, setReferralError] = useState<unknown>(null);
  const [referralResult, setReferralResult] = useState<RedeemReferralResponse | null>(null);

  const applyReferralCode = useCallback(async () => {
    const code = referralCode.trim();
    if (!code) return;
    setReferralSubmitting(true);
    setReferralError(null);
    setReferralResult(null);
    try {
      const result = await redeemReferral(code);
      setReferralResult(result);
      setReferralCode("");
    } catch (error) {
      setReferralError(error);
    } finally {
      setReferralSubmitting(false);
    }
  }, [referralCode]);

  /**
   * Two things collide here and the UI has to be honest about both.
   *
   * `isProfileComplete` on the auth gate is exactly "at least one skill AND at
   * least one service area", so a profile saved without either would drop the
   * contractor back into the onboarding funnel on the next load.
   *
   * But `PATCH /profile` guards EVERY field with a zero check
   * (`if req.FirstName != ""`, `if len(req.Skills) > 0`, `if req.HourlyRate > 0`)
   * — an empty value is read as "not supplied" and the stored one is kept. So
   * the funnel never actually happens: the emptying is silently discarded
   * instead. Warning about a funnel that cannot occur would be a lie, so the
   * confirmation says what really happens — this one change won't stick.
   */
  const emptiedLists = useMemo(() => {
    const parts: string[] = [];
    if (skills.length === 0 && (contractor?.skills.length ?? 0) > 0) parts.push("trades");
    if (areas.length === 0 && (contractor?.serviceAreaGeos.length ?? 0) > 0) {
      parts.push("service areas");
    }
    return parts;
  }, [areas.length, contractor, skills.length]);

  /** Same zero-check semantics, for the optional text fields and the rate. */
  const clearedFields = useMemo(() => {
    const parts: string[] = [];
    if (!phoneNumber.trim() && contractor?.phoneNumber) parts.push("phone number");
    if (!businessName.trim() && contractor?.businessName) parts.push("business name");
    if (!bio.trim() && contractor?.bio) parts.push("bio");
    if (!hourlyRate.trim() && contractor?.hourlyRate) parts.push("hourly rate");
    return parts;
  }, [bio, businessName, contractor, hourlyRate, phoneNumber]);

  const validate = useCallback((): Errors => {
    const next: Errors = {};
    if (!firstName.trim()) next.firstName = "First name is required.";
    if (!lastName.trim()) next.lastName = "Last name is required.";

    const trimmedRate = hourlyRate.trim();
    if (trimmedRate) {
      const parsed = Number(trimmedRate);
      if (!Number.isFinite(parsed) || parsed < 0) {
        next.hourlyRate = "Enter your rate as a number, for example 350.";
      }
    }
    if (bio.length > BIO_LIMIT) next.bio = `Keep your bio under ${BIO_LIMIT} characters.`;
    return next;
  }, [bio, firstName, hourlyRate, lastName]);

  const save = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const trimmedRate = hourlyRate.trim();
      // Only non-empty values are sent. The handler discards empty ones anyway,
      // so including them would put fields on the wire that cannot do anything
      // — and would read as if clearing were supported. `isAvailable` is the one
      // genuine exception: it is a *bool on the Go side, so false is honoured.
      const body: UpdateProfileRequest = {
        isAvailable,
        ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
        ...(phoneNumber.trim() ? { phoneNumber: phoneNumber.trim() } : {}),
        ...(businessName.trim() ? { businessName: businessName.trim() } : {}),
        ...(bio.trim() ? { bio: bio.trim() } : {}),
        ...(skills.length > 0 ? { skills } : {}),
        ...(areas.length > 0 ? { serviceAreaGeos: areas } : {}),
        ...(trimmedRate ? { hourlyRate: Number(trimmedRate) } : {}),
      };

      const updated = await updateProfile(body);
      setContractor(updated);
      toast.show("Your profile is up to date.");
      navigate("/profile");
    } catch (error) {
      setSaveError(error);
    } finally {
      setSaving(false);
    }
  }, [
    areas,
    bio,
    businessName,
    firstName,
    hourlyRate,
    isAvailable,
    lastName,
    navigate,
    phoneNumber,
    setContractor,
    skills,
    toast,
  ]);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAttempted(true);
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstInvalid(formRef.current);
      return;
    }
    if (emptiedLists.length > 0) {
      setConfirmIncomplete(true);
      return;
    }
    void save();
  };

  if (!contractor) {
    return (
      <>
        <PageHead eyebrow="Profile" title="Edit profile" />
        <EmptyState title="No profile to edit">
          Your contractor profile hasn't loaded. Go back to your profile and try again.
        </EmptyState>
      </>
    );
  }

  return (
    <>
      {/* One escape hatch per weight: a back link at the top, the secondary
          Cancel beside Save. This head used to carry a THIRD — a tertiary
          Cancel going to exactly the same place as the one at the bottom. */}
      <PageHead
        eyebrow="Profile"
        title="Edit profile"
        back={
          <Link
            className="back-link"
            to="/profile"
            onClick={(event) => {
              if (!dirty.current) return;
              event.preventDefault();
              setConfirmLeave(true);
            }}
          >
            <ChevronLeftIcon />
            Your profile
          </Link>
        }
      />

      <div className="stack stack--loose">
      <form
        ref={formRef}
        className="stack stack--loose"
        onSubmit={onSubmit}
        onChange={() => {
          dirty.current = true;
        }}
        noValidate
      >
        {saveError ? (
          <Banner tone="danger" title="Your changes weren't saved">
            {errorMessage(saveError)}
          </Banner>
        ) : null}

        {clearedFields.length > 0 ? (
          <Banner tone="warn" title="Some details can't be removed here">
            Shoku keeps your saved {clearedFields.join(", ")} when the box is left empty, so
            emptying it won't take it off your profile. Everything else you change still saves.
            Ask Shoku support if you need one of them removed.
          </Banner>
        ) : null}

        <div className="card card--dense">
          <div className="form">
            <h2 className="headline-sm">About you</h2>
            <div className="field-grid">
              <TextField
                label="First name"
                required
                value={firstName}
                autoComplete="given-name"
                error={errors.firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
              <TextField
                label="Last name"
                required
                value={lastName}
                autoComplete="family-name"
                error={errors.lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            </div>
            <div className="field-grid">
              <TextField
                label="Phone number"
                type="tel"
                value={phoneNumber}
                autoComplete="tel"
                hint="Homeowners see this once you're working together."
                onChange={(event) => setPhoneNumber(event.target.value)}
              />
              <TextField
                label="Business name"
                value={businessName}
                autoComplete="organization"
                hint="Optional. Homeowners see this instead of your own name."
                onChange={(event) => setBusinessName(event.target.value)}
              />
            </div>
            <TextField
              label="Hourly rate (R)"
              inputMode="decimal"
              value={hourlyRate}
              error={errors.hourlyRate}
              hint="In rands per hour. Optional if you quote per job."
              onChange={(event) => setHourlyRate(event.target.value)}
            />
            <TextArea
              label="Bio"
              value={bio}
              maxLength={BIO_LIMIT}
              error={errors.bio}
              hint={`What you do and how long you've been doing it. ${BIO_LIMIT - bio.length} characters left.`}
              onChange={(event) => setBio(event.target.value)}
            />
            <Checkbox
              checked={isAvailable}
              onChange={(event) => setIsAvailable(event.target.checked)}
              label="I'm available for new work"
            />
          </div>
        </div>

        <div className="card card--dense">
          <div className="form">
            <h2 className="headline-sm">Trades</h2>
            <SkillsPicker
              value={skills}
              onChange={(next) => {
                dirty.current = true;
                setSkills(next);
              }}
              error={
                attempted && skills.length === 0
                  ? "Pick at least one trade — homeowners filter jobs by trade."
                  : undefined
              }
            />
          </div>
        </div>

        <div className="card card--dense">
          <div className="form">
            <h2 className="headline-sm">Service areas</h2>
            <ServiceAreaPicker
              value={areas}
              onChange={(next) => {
                dirty.current = true;
                setAreas(next);
              }}
              error={
                attempted && areas.length === 0
                  ? "Add at least one area — you only see jobs near the areas you work."
                  : undefined
              }
            />
          </div>
        </div>

        <div className="cluster">
          <Button type="submit" variant="primary" loading={saving}>
            Save changes
          </Button>
          <Button variant="secondary" onClick={() => navigate("/profile")} disabled={saving}>
            Cancel
          </Button>
        </div>
      </form>

      {/* A separate card, deliberately outside the profile form above: this
          hits POST /referrals/redeem, not PATCH /profile, and has its own
          success/conflict shape. Kept as a standalone action so pressing
          Enter here can't submit the profile form instead. Shows the referrer
          link when already linked, or the input for redemption otherwise. */}
      <div className="card card--dense">
        <div className="form">
          <h2 className="headline-sm">Referral code</h2>
          {contractor.referrerCode ? (
            <p>Referred by {contractor.referrerCode}</p>
          ) : (
            <>
              {referralResult ? (
                <Banner tone="success" title="Referral code applied">
                  {referralResult.linked
                    ? `You're now linked to ${referralResult.referrerName ?? "whoever referred you"}.`
                    : "That code was applied as a referral review on your profile."}
                </Banner>
              ) : null}
              {referralError ? (
                <Banner tone="danger" title="Couldn't apply that code">
                  {errorMessage(referralError)}
                </Banner>
              ) : null}
              <TextField
                label="Referral code"
                hint="If someone referred you to Shoku and you skipped this during sign-up, enter their code here."
                value={referralCode}
                onChange={(event) => setReferralCode(event.target.value.toUpperCase())}
              />
              <div className="cluster">
                <Button
                  type="button"
                  variant="secondary"
                  loading={referralSubmitting}
                  disabled={!referralCode.trim()}
                  onClick={() => void applyReferralCode()}
                >
                  Apply code
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      </div>

      {confirmLeave ? (
        <ConfirmDialog
          title="Leave without saving?"
          confirmLabel="Discard changes"
          cancelLabel="Keep editing"
          destructive
          onCancel={() => setConfirmLeave(false)}
          onConfirm={() => navigate("/profile")}
          body={<p>Your changes haven't been saved. Leaving now throws them away.</p>}
        />
      ) : null}

      {confirmIncomplete ? (
        <ConfirmDialog
          title={`Save without your ${emptiedLists.join(" and ")}?`}
          confirmLabel="Save my other changes"
          cancelLabel="Go back"
          destructive
          confirming={saving}
          onCancel={() => setConfirmIncomplete(false)}
          onConfirm={() => {
            setConfirmIncomplete(false);
            void save();
          }}
          body={
            <>
              <p>
                You've removed every entry under {emptiedLists.join(" and ")}. Shoku needs at least
                one of each — a profile without both counts as unfinished and stops you seeing
                work.
              </p>
              <p>
                Your saved {emptiedLists.join(" and ")} will be kept as they are, and the rest of
                your changes will save normally. To change them, pick the ones you want instead of
                leaving the list empty.
              </p>
            </>
          }
        />
      ) : null}
    </>
  );
}
